import React, { useState } from 'react';
import { SearchIcon, XIcon, MonitorIcon, UsersIcon, CheckIcon, XCircleIcon, ClockIcon, AlertCircleIcon } from 'lucide-react';
import { useConsultationRequests, ConsultationRequest } from './ConsultationRequestsContext';
import { UpdateConsultationStatus } from './UpdateConsultationStatus';
interface ConsultationRequestsListProps {
  onViewDetails: (request: ConsultationRequest) => void;
}
export function ConsultationRequestsList({
  onViewDetails
}: ConsultationRequestsListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [consultantFilter, setConsultantFilter] = useState<number | null>(null);
  const {
    consultationRequests,
    loading
  } = useConsultationRequests();
  const [selectedRequest, setSelectedRequest] = useState<ConsultationRequest | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  // Get unique consultants from the requests
  const uniqueConsultants = Array.from(new Set(consultationRequests.map(request => request.consultantId))).map(consultantId => {
    const request = consultationRequests.find(r => r.consultantId === consultantId);
    return {
      id: consultantId,
      name: request ? request.consultantName : 'غير معروف'
    };
  });
  const filteredRequests = consultationRequests.filter(request => {
    const matchesSearch = searchQuery === '' || request.userName.includes(searchQuery) || request.userPhone.includes(searchQuery) || request.userEmail.includes(searchQuery) || request.topic.includes(searchQuery) || request.consultantName.includes(searchQuery);
    const matchesStatus = statusFilter === null || request.status === statusFilter;
    const matchesConsultant = consultantFilter === null || request.consultantId === consultantFilter;
    return matchesSearch && matchesStatus && matchesConsultant;
  });
  const handleUpdateStatus = (request: ConsultationRequest) => {
    setSelectedRequest(request);
    setShowStatusModal(true);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-558">
            <ClockIcon className="h-3 w-3 ml-1" data-id="element-559" />
            قيد الانتظار
          </span>;
      case 'confirmed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-560">
            <CheckIcon className="h-3 w-3 ml-1" data-id="element-561" />
            مؤكد
          </span>;
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-562">
            <CheckIcon className="h-3 w-3 ml-1" data-id="element-563" />
            مكتمل
          </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-564">
            <XCircleIcon className="h-3 w-3 ml-1" data-id="element-565" />
            ملغي
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-566">
            <AlertCircleIcon className="h-3 w-3 ml-1" data-id="element-567" />
            غير معروف
          </span>;
    }
  };
  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-568">
            تم الدفع
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-569">
            بانتظار الدفع
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-570">
            غير معروف
          </span>;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  if (loading) {
    return <div className="bg-white rounded-lg shadow-md p-6" data-id="element-571">
        <div className="flex justify-center items-center h-64" data-id="element-572">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-sky-600 border-t-transparent" data-id="element-573"></div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-lg shadow-md p-6" data-id="element-574">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4" data-id="element-575">
        <h2 className="text-xl font-bold text-sky-900" data-id="element-576">طلبات الاستشارة</h2>
        <div className="flex gap-4 w-full md:w-auto" data-id="element-577">
          <div className="relative flex-1 md:w-64" data-id="element-578">
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="بحث عن طلب..." className="w-full px-4 py-2 pr-10 rounded-md border border-slate-200 focus:border-sky-300 focus:ring focus:ring-sky-100 focus:outline-none" data-id="element-579" />
            <SearchIcon className="absolute top-2.5 right-3 h-5 w-5 text-slate-400" data-id="element-580" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute top-2.5 left-3 h-5 w-5 text-slate-400 hover:text-slate-600" data-id="element-581">
                <XIcon className="h-5 w-5" data-id="element-582" />
              </button>}
          </div>
        </div>
      </div>
      <div className="mb-6 flex flex-wrap gap-2" data-id="element-583">
        <div className="ml-4" data-id="element-584">
          <span className="block text-sm font-medium text-slate-700 mb-1" data-id="element-585">
            تصفية حسب الحالة:
          </span>
          <div className="flex flex-wrap gap-2" data-id="element-586">
            <button onClick={() => setStatusFilter(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === null ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-700 hover:bg-sky-50'}`} data-id="element-587">
              الكل
            </button>
            <button onClick={() => setStatusFilter('pending')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700 hover:bg-yellow-50'}`} data-id="element-588">
              قيد الانتظار
            </button>
            <button onClick={() => setStatusFilter('confirmed')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === 'confirmed' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-blue-50'}`} data-id="element-589">
              مؤكد
            </button>
            <button onClick={() => setStatusFilter('completed')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-green-50'}`} data-id="element-590">
              مكتمل
            </button>
            <button onClick={() => setStatusFilter('cancelled')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700 hover:bg-red-50'}`} data-id="element-591">
              ملغي
            </button>
          </div>
        </div>
        <div data-id="element-592">
          <span className="block text-sm font-medium text-slate-700 mb-1" data-id="element-593">
            تصفية حسب الاستشاري:
          </span>
          <div className="flex flex-wrap gap-2" data-id="element-594">
            <button onClick={() => setConsultantFilter(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${consultantFilter === null ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-700 hover:bg-sky-50'}`} data-id="element-595">
              الكل
            </button>
            {uniqueConsultants.map(consultant => <button key={consultant.id} onClick={() => setConsultantFilter(consultant.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${consultantFilter === consultant.id ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-purple-50'}`} data-id="element-596">
                {consultant.name}
              </button>)}
          </div>
        </div>
      </div>
      {filteredRequests.length === 0 ? <div className="text-center py-12 bg-slate-50 rounded-lg" data-id="element-597">
          <p className="text-slate-500 mb-2" data-id="element-598">
            لا يوجد طلبات استشارة مطابقة لبحثك
          </p>
          {(searchQuery || statusFilter || consultantFilter) && <button onClick={() => {
        setSearchQuery('');
        setStatusFilter(null);
        setConsultantFilter(null);
      }} className="text-sky-600 hover:text-sky-800 font-medium" data-id="element-599">
              عرض جميع الطلبات
            </button>}
        </div> : <div className="overflow-x-auto" data-id="element-600">
          <table className="min-w-full divide-y divide-slate-200" data-id="element-601">
            <thead className="bg-slate-50" data-id="element-602">
              <tr data-id="element-603">
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-604">
                  المستخدم
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-605">
                  الاستشاري
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-606">
                  الموعد
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-607">
                  النوع
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-608">
                  الحالة
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-609">
                  الدفع
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-610">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200" data-id="element-611">
              {filteredRequests.map(request => <tr key={request.id} className="hover:bg-slate-50" data-id="element-612">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-613">
                    <div className="flex flex-col" data-id="element-614">
                      <div className="text-sm font-medium text-slate-900" data-id="element-615">
                        {request.userName}
                      </div>
                      <div className="text-sm text-slate-500" data-id="element-616">
                        {request.userPhone}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-617">
                        {request.userEmail}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-618">
                    <div className="text-sm text-slate-900" data-id="element-619">
                      {request.consultantName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-620">
                    <div className="text-sm text-slate-900" data-id="element-621">
                      {request.date} - {request.time}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-622">
                      {formatDate(request.createdAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-623">
                    <div className="text-sm text-slate-900" data-id="element-624">
                      {request.consultationType === 'online' ? <div className="flex items-center" data-id="element-625">
                          <MonitorIcon className="h-4 w-4 text-green-600 ml-1" data-id="element-626" />
                          <span data-id="element-627">أونلاين</span>
                        </div> : <div className="flex items-center" data-id="element-628">
                          <UsersIcon className="h-4 w-4 text-purple-600 ml-1" data-id="element-629" />
                          <span data-id="element-630">وجاهي</span>
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-631">
                    {getStatusBadge(request.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-632">
                    <div className="flex flex-col items-start" data-id="element-633">
                      <div className="text-sm text-slate-900 mb-1" data-id="element-634">
                        {request.paymentMethod === 'cliq' ? 'CliQ' : request.paymentMethod === 'bank' ? 'حوالة بنكية' : request.paymentMethod === 'cash' ? 'نقداً' : request.paymentMethod}
                      </div>
                      {getPaymentStatusBadge(request.paymentStatus)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium" data-id="element-635">
                    <div className="flex justify-center space-x-2 rtl:space-x-reverse" data-id="element-636">
                      <button onClick={() => handleUpdateStatus(request)} className="text-sky-600 hover:text-sky-900 p-1" title="تغيير الحالة" data-id="element-637">
                        <CheckIcon className="h-5 w-5" data-id="element-638" />
                      </button>
                      <button onClick={() => onViewDetails(request)} className="text-amber-600 hover:text-amber-900 p-1" title="عرض التفاصيل" data-id="element-639">
                        <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-md text-xs" data-id="element-640">
                          التفاصيل
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}
      {showStatusModal && selectedRequest && <UpdateConsultationStatus request={selectedRequest} onClose={() => {
      setShowStatusModal(false);
      setSelectedRequest(null);
    }} data-id="element-641" />}
    </div>;
}